"use client";

import { useState, useEffect } from "react";
import { Eye, EyeOff, Lock, User, ArrowRight, Sparkles, CheckCircle, Sun, Moon } from "lucide-react";
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "../contexts/AuthContext";
import { useApi } from "../hooks/useApi";
import axios from 'axios';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from 'react-toastify';
import MainNavbar from "../components/layout/MainNavbar";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user: authUser, loading: authLoading } = useAuth();
  const { post, loading: apiLoading } = useApi();
  
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [theme, setTheme] = useState('light');
  const [isFlipped, setIsFlipped] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [formData, setFormData] = useState({
    Email: "",
    Password: "",
    otp: "",
    forgotEmail: "",
    signupName: "",
    signupEmail: "",
    signupPassword: "",
    signupConfirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const LAST_VISITED_ROUTE_KEY = "lastVisitedRoute";

  const getDefaultRouteForUser = (userData) => {
    if (userData?.role === "admin" || userData?.role === "super_admin" || userData?.role === "superadmin") {
      return "/admin/dashboard";
    }
    if (userData?.role === "customer" || userData?.role === "user") {
      return "/customer/dashboard";
    }
    return "/home";
  };

  const isAllowedRouteForUser = (route, userData) => {
    if (!route || !userData) return false;
    if (route.startsWith("/admin")) {
      return userData.role === "admin" || userData.role === "super_admin" || userData.role === "superadmin";
    }
    if (route.startsWith("/customer")) {
      return userData.role === "customer" || userData.role === "user";
    }
    // Only restore protected app routes after login; avoid sending users back to public pages like /home.
    return false;
  };

  const getPostLoginRoute = (userData) => {
    const isAdminUser =
      userData?.role === "admin" ||
      userData?.role === "super_admin" ||
      userData?.role === "superadmin";
    const isCustomerUser = userData?.role === "customer" || userData?.role === "user";

    const fromPathname = location.state?.from?.pathname;
    const fromSearch = location.state?.from?.search || "";
    const fromHash = location.state?.from?.hash || "";
    const fromRoute = fromPathname ? `${fromPathname}${fromSearch}${fromHash}` : null;
    const savedRoute = localStorage.getItem(LAST_VISITED_ROUTE_KEY);
    const blockedRoutes = new Set(["/", "/login", "/unauthorized"]);

    if (fromRoute && !blockedRoutes.has(fromRoute) && isAllowedRouteForUser(fromRoute, userData)) {
      const safe = fromRoute;
      if (isCustomerUser && safe.startsWith("/admin")) return "/customer/dashboard";
      if (isAdminUser && safe.startsWith("/customer")) return "/admin/dashboard";
      return safe;
    }

    if (savedRoute && !blockedRoutes.has(savedRoute) && isAllowedRouteForUser(savedRoute, userData)) {
      const safe = savedRoute;
      if (isCustomerUser && safe.startsWith("/admin")) return "/customer/dashboard";
      if (isAdminUser && safe.startsWith("/customer")) return "/admin/dashboard";
      return safe;
    }

    const def = getDefaultRouteForUser(userData);
    // Extra safety: role must match the routed area.
    if (isCustomerUser && def.startsWith("/admin")) return "/customer/dashboard";
    if (isAdminUser && def.startsWith("/customer")) return "/admin/dashboard";
    return def;
  };

  // Function to try different payload formats for production API
  const tryLoginWithDifferentPayloads = async (email, password) => {
    const payloadFormats = [
      { email: email, password: password },
      { username: email, password: password },
      { Email: email, Password: password },
      { user_email: email, user_password: password },
      { login: email, pass: password }
    ];
    let lastError = null;

    for (const payload of payloadFormats) {
      try {
        
        const response = await post('/login', payload);
        
        if (response && response.token) {
          return response;
        }
      } catch (error) {
        lastError = error;
        continue;
      }
    }

    throw lastError || new Error('All payload formats failed');
  };

  // Theme management
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  // Update loading state to include API loading
  useEffect(() => {
    setLoading(apiLoading);
  }, [apiLoading]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (isAuthenticated() && authUser) {
      const redirectPath = getPostLoginRoute(authUser);
      localStorage.setItem(LAST_VISITED_ROUTE_KEY, redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [authLoading, isAuthenticated, authUser, navigate]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear errors when user starts typing
    if (errors[e.target.name] || errors.general) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        if (e.target.name === 'Email' || e.target.name === 'Password') {
          delete newErrors.general;
        }
        return newErrors;
      });
    }
  };

  const buildInputClasses = (hasError, extraClasses = '') => {
    const baseClasses =
      theme === 'dark'
        ? 'bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/40 focus:ring-white/20'
        : 'bg-white/60 border-gray-300 text-gray-800 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-200';

    const errorClasses =
      theme === 'dark'
        ? 'border-red-600 bg-red-500/10 focus:border-red-600 focus:ring-red-500/40 ring-2 ring-red-400/50'
        : 'border-red-500 bg-red-100 focus:border-red-600 focus:ring-red-200 ring-2 ring-red-500/50';

    return `transition-all duration-500 ${baseClasses} ${extraClasses} ${
      hasError ? errorClasses : ''
    }`.trim();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isFlipped) {
        // Forgot password form
        handleForgotPassword();
      } else if (activeTab === 'login') {
        if (userDetails?.showOtpInput) {
          handleVerifyOtp();
        } else {
          handleValidateUser();
        }
      } else if (activeTab === 'help') {
        // Handle help tab if needed
      }
    }
  };

  const validateInputs = (checkOtp = false) => {
    const { Email, Password, otp } = formData;
    const newErrors = {};
    if (!Email) {
      newErrors.Email = "Email is required";
    }
    if (!Password) {
      newErrors.Password = "Password is required";
    }
    if (checkOtp && !otp) {
      newErrors.otp = "OTP is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleValidateUser = async () => {
    try {
      setErrors({});
      if (!validateInputs()) {
        return;
      }
      const { Email, Password } = formData;

      // Debug: Log the request payload

      // Try different payload formats to see which one works
      const loginPayload = { 
        email: Email, 
        password: Password 
      };
      
      
      // Try the primary payload format first, then try different formats
      let response;
      try {
        response = await post('/login', loginPayload);
      } catch (primaryError) {
        response = await tryLoginWithDifferentPayloads(Email, Password);
      }
      
      
      // Handle different response formats from production API
      const candidateSources = [
        response,
        response?.data,
        response?.payload,
        response?.result,
        response?.response,
        response?.data?.data
      ].filter((src) => src && typeof src === 'object');

      const pickUserObject = (maybeUser) => {
        if (!maybeUser || typeof maybeUser !== 'object') return null;
        if (maybeUser.user) return pickUserObject(maybeUser.user);
        if (maybeUser.profile) return pickUserObject(maybeUser.profile);
        if (maybeUser.account) return pickUserObject(maybeUser.account);

        const userLikeKeys = ['role', 'email', 'name', 'id', '_id'];
        const hasUserLikeKey = userLikeKeys.some((key) => Object.prototype.hasOwnProperty.call(maybeUser, key));
        return hasUserLikeKey ? maybeUser : null;
      };

      let token;
      let user;

      for (const source of candidateSources) {
        if (!token) {
          token = source?.token || source?.access_token || source?.jwt || source?.authToken || source?.accessToken;
        }

        if (!user) {
          user = pickUserObject(source);
        }

        if (!user) {
          const nestedUser = pickUserObject(source?.data);
          if (nestedUser) {
            user = nestedUser;
          }
        }

        if (!token) {
          const nestedTokenSource = source?.data && typeof source.data === 'object' ? source.data : null;
          if (nestedTokenSource) {
            token = nestedTokenSource.token || nestedTokenSource.access_token || nestedTokenSource.jwt || nestedTokenSource.authToken || nestedTokenSource.accessToken;
          }
        }

        if (token && user) {
          break;
        }
      }

      
      if (response && token && user) {
        
        // Use AuthContext to login
        login(user, token);
        
        // Redirect to previous route (if valid) or role default route.
        const redirectPath = getPostLoginRoute(user);
        localStorage.setItem(LAST_VISITED_ROUTE_KEY, redirectPath);
        if (user.role === "admin" || user.role === "super_admin" || user.role === "superadmin") {
          localStorage.setItem("loggedAs", "admin");
        } else if (user.role === "customer" || user.role === "user") {
          localStorage.setItem("loggedAs", "customer");
        }
        navigate(redirectPath, { replace: true });
        
        return;
      } else {
        setErrors({ general: "Invalid credentials. Please check your email and password and try again." });
      }
      
    } catch (error) {
      
      // Show more detailed error information
      let errorMessage = 'Invalid credentials. Please check your email and password and try again.';
      
      // Check for specific error status codes
      if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.response?.status === 422) {
        errorMessage = error.response.data?.message || 'Invalid credentials';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Service unavailable. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.isCorsError || error.code === 'ERR_NETWORK' || !error.response) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      }
      
      setErrors({ 
        general: errorMessage
      });
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setLoading(true);
      setErrors({});
      if (!validateInputs(true)) {
        setLoading(false);
        return;
      }
      // Simulate OTP verification (replace with real API call if needed)
      if (formData.otp === userDetails?.otp) {
        // OTP verified, redirect based on user
        localStorage.setItem("loggedAs", "admin");
         navigate("/superadmin");
        return;       
        if (formData.Email === "customer1@trackwheelz.com") {
          localStorage.setItem("loggedAs", "customer");
          navigate("/customer");
          return;
          if(formData.Email === "admin@trackwheelz.com") {
            localStorage.setItem("loggedAs", "admin");
            navigate("/admin");
            return;
          }
        }
      } else {
        setErrors({ otp: "Invalid OTP" });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrors({ otp: "OTP verification failed" });
    }
  };

  const handleForgotPassword = async () => {
    try {
      setLoading(true);
      setErrors({});
      
      if (!formData.forgotEmail) {
        setErrors({ forgotEmail: "Email is required" });
        setLoading(false);
        return;
      }

      // Here you would call your forgot password API
      // For now, we'll just simulate the process
      
      // Simulate API call delay
      setTimeout(() => {
        setLoading(false);
        alert("If an account with that email exists, you will receive a password reset link shortly.");
        // Flip back to login form
        setIsFlipped(false);
      }, 2000);
      
    } catch (error) {
      setLoading(false);
      setErrors({ forgotEmail: "Failed to send reset email" });
    }
  };

  const handleBackToLogin = () => {
    setIsFlipped(false);
    setErrors({});
  };

  const handleShowSignup = () => {
    setErrors({});
    setShowSignup(true);
    setIsTransitioning(false);
  };

  const handleBackToLoginFromSignup = () => {
    setErrors({});
    setShowSignup(false);
    setIsTransitioning(false);
  };

  const handleSignup = async () => {
    try {
      setLoading(true);
      setErrors({});
      
      // Validate signup fields
      const newErrors = {};
      if (!formData.signupName) {
        newErrors.signupName = "Name is required";
      }
      if (!formData.signupEmail) {
        newErrors.signupEmail = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.signupEmail)) {
        newErrors.signupEmail = "Email is invalid";
      }
      if (!formData.signupPassword) {
        newErrors.signupPassword = "Password is required";
      } else if (formData.signupPassword.length < 6) {
        newErrors.signupPassword = "Password must be at least 6 characters";
      }
      if (!formData.signupConfirmPassword) {
        newErrors.signupConfirmPassword = "Please confirm your password";
      } else if (formData.signupPassword !== formData.signupConfirmPassword) {
        newErrors.signupConfirmPassword = "Passwords do not match";
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }

      // Prepare signup payload
      const signupPayload = {
        name: formData.signupName,
        email: formData.signupEmail,
        password: formData.signupPassword
      };


      // Call the signup API
      const response = await post('/customer-account/register', signupPayload);


      // Handle response
      if (response?.success && response?.data) {
        
        // Show success message and redirect to login
        setLoading(false);
        toast.success("Signup successful! Please login with your credentials.");
        handleBackToLoginFromSignup();
        
        // Optionally pre-fill the email in the login form
        setFormData(prev => ({
          ...prev,
          Email: formData.signupEmail
        }));
      } else {
        setLoading(false);
        setErrors({ general: "Signup failed. Please try again." });
      }
      
    } catch (error) {
      
      setLoading(false);
      
      let errorMessage = 'An error occurred during signup. Please try again.';
      
      if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || 'Invalid signup data. Please check your information.';
      } else if (error.response?.status === 409) {
        errorMessage = 'An account with this email already exists. Please login instead.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    }
  };

  // Function to test production API with different payload formats
  const testProductionAPI = async () => {
    
    const testEmail = 'test@example.com';
    const testPassword = 'test123';
    
    const payloadFormats = [
      { email: testEmail, password: testPassword },
      { username: testEmail, password: testPassword },
      { Email: testEmail, Password: testPassword },
      { user_email: testEmail, user_password: testPassword },
      { login: testEmail, pass: testPassword }
    ];

    for (const payload of payloadFormats) {
      try {
        const response = await post('/login', payload);
        return;
      } catch (error) {
        if (error.response) {
        } else if (error.code === 'ERR_NETWORK') {
        } else {
        }
      }
    }
    
  };

  return (
    <>
      <style>{`
        @keyframes progress {
          0% {
            width: 0%;
          }
          100% {
            width: 100%;
          }
        }
        .animate-progress {
          animation: progress 1.5s ease-in-out infinite;
        }
        @keyframes smoothFadeOut {
          0% {
            opacity: 1;
            transform: scale(1) translateY(0);
            filter: blur(0px);
          }
          100% {
            opacity: 0;
            transform: scale(0.95) translateY(-20px);
            filter: blur(4px);
          }
        }
        @keyframes smoothFadeIn {
          0% {
            opacity: 0;
            transform: scale(0.95) translateY(20px);
            filter: blur(4px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
            filter: blur(0px);
          }
        }
        .card-dismiss {
          animation: smoothFadeOut 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .card-zoom-in {
          animation: smoothFadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
      `}</style>
      <div className={`min-h-screen flex relative overflow-hidden transition-colors duration-500 text-sm sm:text-base ${
        theme === 'dark' 
          ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e]' 
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
      }`}>
      <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Login" />

      {/* Theme Toggle Button - Fixed Position */}
      <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-50 hidden">
        <Button
          onClick={toggleTheme}
          variant="ghost"
          size="sm"
          className={`p-2 sm:p-3 rounded-full transition-all duration-500 ${
            theme === 'dark'
              ? 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-md border border-white/20'
              : 'bg-black/10 text-black hover:bg-black/20 backdrop-blur-md border border-black/20'
          }`}
        >
          {theme === 'dark' ? (
            <Sun className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-500 rotate-0" />
          ) : (
            <Moon className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-500 rotate-0" />
          )}
        </Button>
      </div>

      {/* Background Elements */}
      <div className="absolute inset-0">
        {theme === 'dark' ? (
          // Dark theme background - Purple to Dark gradient (left to right)
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-[#483D8B] to-[#2d1b4e]"></div>
          </>
        ) : (
          // Light theme background
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"></div>
            <div className="absolute top-20 left-20 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-indigo-200/30 rounded-full blur-3xl animate-pulse delay-500"></div>
          </>
        )}
      </div>

      <div className="w-full min-h-screen flex items-center justify-center p-2 sm:p-4 relative z-10 pt-16 sm:pt-18">
        <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
          
          {/* Left Side - Branding & Info */}
          <div className="hidden md:block space-y-6 md:space-y-8 mt-4 md:mt-8 lg:mt-12">
            <div className="space-y-6">
              <div className="flex justify-center md:justify-start">
                <img 
                  src="/logo.png" 
                  alt="TrackYourWheelz Logo" 
                  className="w-80 sm:w-50 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              
              <div className="space-y-4">
                <h2 className={`text-2xl font-bold leading-tight transition-colors duration-500 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-800'
                }`}>
                  Secure Access to Your
                  <span className={`block transition-all duration-500 ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                  }`}>
                    Vehicle Fleet
                  </span>
                </h2>
                <p className={`text-sm leading-relaxed transition-colors duration-500 ${
                  theme === 'dark' ? 'text-white/90' : 'text-gray-600'
                }`}>
                  Monitor, track, and manage your entire vehicle fleet with our advanced GPS tracking system. 
                </p>
              </div>
            </div>

            {/* Feature highlights */}
            <div className="space-y-4 mt-8">
              {[
                { icon: CheckCircle, color: 'green', text: 'Real-time GPS tracking' },
                { icon: CheckCircle, color: 'blue', text: 'Advanced analytics & reports' },
                { icon: CheckCircle, color: 'purple', text: 'Multi-user access control' }
              ].map((feature, index) => (
                <div key={index} className={`flex items-center space-x-3 backdrop-blur-sm rounded-xl p-3 border transition-all duration-500 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-white/10 border-white/20 hover:bg-white/15'
                    : 'bg-white/60 border-white/40 hover:bg-white/80'
                }`}>
                  <div className={`p-2 rounded-full transition-all duration-500 ${
                    theme === 'dark'
                      ? `bg-${feature.color}-400/20`
                      : `bg-${feature.color}-100`
                  }`}>
                    <feature.icon className={`h-5 w-5 transition-colors duration-500 ${
                      theme === 'dark' ? `text-${feature.color}-300` : `text-${feature.color}-600`
                    }`} />
                  </div>
                  <span className={`transition-colors duration-500 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-700'
                  }`}>{feature.text}</span>
                </div>
              ))}
            </div>
          </div>


          {/* Right Side - Login/Signup Form */}
          <div className="w-full relative mt-4 md:mt-8 lg:mt-16" style={{ perspective: '1000px', minHeight: '500px' }}>
            {/* Login Form */}
            {!showSignup && (
              <div className="w-full card-zoom-in">
                <div className={`relative w-full transition-transform duration-700 ${
                  isFlipped ? 'transform rotate-y-180' : ''
                }`} style={{ 
                  transformStyle: 'preserve-3d',
                  transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                }}>
                  {/* Front Side - Login Form */}
                  <div className={`w-full backdrop-blur-xl border shadow-2xl transition-all duration-500 rounded-xl sm:rounded-2xl ${
                    theme === 'dark'
                      ? 'bg-white/10 border-white/20'
                      : 'bg-white/80 border-white/30'
                  }`} style={{ backfaceVisibility: 'hidden' }}>
              <div className="sm:hidden flex justify-center pt-4">
                <img
                  src="/logo1.png"
                  alt="TrackYourWheelz Logo"
                  className="w-32 h-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <CardHeader className="text-center space-y-1.5 sm:space-y-2.5 p-3 sm:p-4 pt-5 sm:pt-6">
                <div className="space-y-1">
                  <CardTitle className={`text-xl sm:text-2xl font-bold transition-colors duration-500 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                  }`}>
                    Welcome Back
                  </CardTitle>
                  <CardDescription className={`text-xs sm:text-sm transition-colors duration-500 ${
                    theme === 'dark' ? 'text-white/80' : 'text-gray-600'
                  }`}>
                    Sign in to access your dashboard
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-4">
                <Tabs value={activeTab} onValueChange={(value) => {
                  setActiveTab(value);
                  setErrors({});
                }} className="w-full">
                  <TabsList className={`grid w-full grid-cols-2 transition-all duration-500 ${
                    theme === 'dark'
                      ? 'bg-white/10 border border-white/20'
                      : 'bg-white/60 border border-white/40'
                  }`}>
                    <TabsTrigger 
                      value="login" 
                      className={`transition-all border-r-0 border-b-0 border-t-0 duration-300 ${
                        theme === 'dark'
                          ? 'data-[state=active]:bg-white/20 data-[state=active]:text-white'
                          : 'data-[state=active]:bg-white data-[state=active]:text-gray-800'
                      }`}
                    >
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger 
                      value="help" 
                      className={`transition-all duration-300 ${
                        theme === 'dark'
                          ? 'data-[state=active]:bg-white/20 data-[state=active]:text-white'
                          : 'data-[state=active]:bg-white data-[state=active]:text-gray-800'
                      }`}
                    >
                      Need Help?
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="login" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
                    {errors?.general && (
                      <Alert variant="destructive" className={`${
                        theme === 'dark'
                          ? 'bg-red-500/20 border-red-400/30 text-red-200'
                          : 'bg-red-50 border-red-200 text-red-800'
                      }`}>
                        <AlertDescription className="whitespace-pre-line text-xs sm:text-sm">
                          {errors.general}
                        </AlertDescription>
                      </Alert>
                    )}
                   
                    <div className="space-y-2 sm:space-y-3">
                      <div className="space-y-1 sm:space-y-2">
                        <Label htmlFor="Email" className={`text-sm font-semibold flex items-center gap-2 transition-colors duration-500 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-700'
                        }`}>
                          <User className={`h-4 w-4 transition-colors duration-500 ${
                            theme === 'dark' ? 'text-white/70' : 'text-gray-500'
                          }`} />
                          Username or Email
                        </Label>
                        <Input
                          id="Email"
                          type="text"
                          name="Email"
                          placeholder="Enter your username or email"
                          value={formData.Email}
                          onChange={handleChange}
                          onKeyPress={handleKeyPress}
                          disabled={userDetails?.showOtpInput}
                          className={buildInputClasses(Boolean(errors?.Email))}
                        />
                        {errors?.Email && (
                          <Alert variant="destructive" className={`${
                            theme === 'dark'
                              ? 'bg-red-500/20 border-red-400/30 text-red-200'
                              : 'bg-red-50 border-red-200 text-red-800'
                          }`}>
                            <AlertDescription className="text-xs sm:text-sm">
                              {errors.Email}
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="Password" className={`text-sm font-semibold flex items-center gap-2 transition-colors duration-500 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-700'
                        }`}>
                          <Lock className={`h-4 w-4 transition-colors duration-500 ${
                            theme === 'dark' ? 'text-white/70' : 'text-gray-500'
                          }`} />
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="Password"
                            type={showPassword ? "text" : "password"}
                            name="Password"
                            placeholder="Enter your password"
                            value={formData.Password}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            disabled={userDetails?.showOtpInput}
                            className={buildInputClasses(Boolean(errors?.Password), 'pr-12')}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={`absolute right-0 top-0 h-full px-3 py-2 transition-all duration-500 ${
                              theme === 'dark'
                                ? 'hover:bg-white/10 text-white'
                                : 'hover:bg-gray-100 text-gray-500'
                            }`}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </Button>
                        </div>
                        {errors?.Password && (
                          <Alert variant="destructive" className={`${
                            theme === 'dark'
                              ? 'bg-red-500/20 border-red-400/30 text-red-200'
                              : 'bg-red-50 border-red-200 text-red-800'
                          }`}>
                            <AlertDescription className="text-xs sm:text-sm">
                              {errors.Password}
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>

                      {userDetails?.showOtpInput && (
                        <div className="space-y-2">
                          <Label htmlFor="otp" className={`text-sm font-semibold transition-colors duration-500 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-700'
                          }`}>
                            OTP Verification
                          </Label>
                          <Input
                            id="otp"
                            type="text"
                            name="otp"
                            placeholder="Enter 6-digit OTP"
                            value={formData.otp}
                            onChange={handleChange}
                            onKeyPress={handleKeyPress}
                            className={`transition-all duration-500 ${
                              theme === 'dark'
                                ? 'bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/40 focus:ring-white/20'
                                : 'bg-white/60 border-gray-300 text-gray-800 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-200'
                            }`}
                          />
                          {errors?.otp && (
                            <Alert variant="destructive" className="bg-red-500/20 border-red-400/30 text-red-200">
                              <AlertDescription className="text-xs sm:text-sm">
                                {errors?.otp}
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="remember-me"
                            checked={rememberMe}
                            onCheckedChange={setRememberMe}
                            className={`transition-all duration-300 ${
                              theme === 'dark' 
                                ? 'data-[state=checked]:bg-blue-400 data-[state=unchecked]:bg-white/20' 
                                : 'data-[state=checked]:bg-blue-500'
                            }`}
                          />
                          <Label htmlFor="remember-me" className={`text-sm transition-colors duration-500 ${
                            theme === 'dark' ? 'text-white/80' : 'text-gray-600'
                          }`}>
                            Remember me
                          </Label>
                        </div>
                        <Button 
                          variant="link" 
                          className={`text-sm p-0 transition-colors duration-500 ${
                            theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'
                          }`}
                          onClick={() => setIsFlipped(true)}
                        >
                          Forgot password?
                        </Button>
                      </div>
                    </div>

                    <div className="hidden sm:block">
                      {!userDetails?.showOtpInput ? (
                        <Button
                          onClick={handleValidateUser}
                          type="button"
                          className={`w-full h-9 sm:h-10 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] text-sm ${
                            theme === 'dark'
                              ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                              : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                          }`}
                          disabled={loading}
                        >
                          {loading ? (
                            <div className="w-full flex flex-col items-center justify-center space-y-2">
                              <div className="relative w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                                <div className="absolute h-full left-0 top-0 animate-progress bg-gradient-to-r from-blue-500/50 to-purple-500/50 rounded-full">
                                  <img
                                    src="/logo1.png"
                                    alt="Loading"
                                    className="h-full w-auto object-contain opacity-80 absolute right-0"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              </div>
                              <span className="text-xs">Signing In...</span>
                            </div>
                          ) : (
                            <>
                              Sign In
                              <ArrowRight className="ml-2 h-5 w-5" />
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={handleVerifyOtp}
                          type="button"
                          className={`w-full h-9 sm:h-10 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] text-sm ${
                            theme === 'dark'
                              ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                              : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                          }`}
                          disabled={loading}
                        >
                          {loading ? (
                            <div className="w-full flex flex-col items-center justify-center space-y-2">
                              <div className="relative w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                                <div className="absolute h-full w-1/3 animate-progress">
                                  <img
                                    src="/logo1.png"
                                    alt="Loading"
                                    className="h-full w-full object-contain opacity-80"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              </div>
                              <span className="text-xs">Verifying...</span>
                            </div>
                          ) : (
                            <>
                              Verify OTP
                              <ArrowRight className="ml-2 h-5 w-5" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    <div className="sm:hidden">
                      {!userDetails?.showOtpInput ? (
                        <Button
                          onClick={handleValidateUser}
                          type="button"
                          className={`w-full h-12 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01] text-base ${
                            theme === 'dark'
                              ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                              : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                          }`}
                          disabled={loading}
                        >
                          {loading ? (
                            <div className="w-full flex flex-col items-center justify-center space-y-2">
                              <div className="relative w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                                <div className="absolute h-full left-0 top-0 animate-progress bg-gradient-to-r from-blue-500/50 to-purple-500/50 rounded-full">
                                  <img
                                    src="/logo1.png"
                                    alt="Loading"
                                    className="h-full w-auto object-contain opacity-80 absolute right-0"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              </div>
                              <span className="text-xs">Signing In...</span>
                            </div>
                          ) : (
                            <>
                              Sign In
                              <ArrowRight className="ml-2 h-5 w-5" />
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={handleVerifyOtp}
                          type="button"
                          className={`w-full h-12 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01] text-base ${
                            theme === 'dark'
                              ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                              : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                          }`}
                          disabled={loading}
                        >
                          {loading ? (
                            <div className="w-full flex flex-col items-center justify-center space-y-2">
                              <div className="relative w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                                <div className="absolute h-full w-1/3 animate-progress">
                                  <img
                                    src="/logo1.png"
                                    alt="Loading"
                                    className="h-full w-full object-contain opacity-80"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              </div>
                              <span className="text-xs">Verifying...</span>
                            </div>
                          ) : (
                            <>
                              Verify OTP
                              <ArrowRight className="ml-2 h-5 w-5" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>


                    <Separator className={`my-4 transition-colors duration-500 ${
                      theme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
                    }`} />
                    
                    <div className="text-center space-y-1">
                      <p className={`text-sm transition-colors duration-500 ${
                        theme === 'dark' ? 'text-white/80' : 'text-gray-600'
                      }`}>
                        Don't have an account?{" "}
                        <Button 
                          variant="link" 
                          onClick={handleShowSignup}
                          className={`p-0 font-medium transition-colors duration-500 ${
                            theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'
                          }`}
                        >
                          Sign Up
                        </Button>
                      </p>
                      
                      {/* Debug buttons - remove these in production */}
                      <div className="flex gap-2 justify-center">
                        
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="help" className="space-y-4 mt-6">
                    <Alert className={`transition-all duration-500 ${
                      theme === 'dark'
                        ? 'bg-white/10 border-white/20 text-white'
                        : 'bg-blue-50 border-blue-200 text-blue-800'
                    }`}>
                      <AlertDescription>
                        Need help signing in? Contact our support team at support@trackyourwheelz.com
                      </AlertDescription>
                    </Alert>
                    
                    <div className={`space-y-3 text-sm transition-colors duration-500 ${
                      theme === 'dark' ? 'text-white/80' : 'text-gray-600'
                    }`}>
                      <p>• Make sure your username and password are correct</p>
                      <p>• Check if Caps Lock is turned off</p>
                      <p>• Clear your browser cache and cookies</p>
                      <p>• Try using a different browser</p>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </div>

            {/* Back Side - Forgot Password Form */}
            <div className={`absolute inset-0 w-full backdrop-blur-xl border shadow-2xl transition-all duration-500 rounded-xl sm:rounded-2xl ${
              theme === 'dark'
                ? 'bg-white/10 border-white/20'
                : 'bg-white/80 border-white/30'
            }`} style={{ 
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)'
            }}>
              <CardHeader className="text-center space-y-3 sm:space-y-4 p-4 sm:p-6">
                <div className="flex justify-center">
                  <div className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-500 ${
                    theme === 'dark'
                      ? 'bg-white/20 border border-white/30'
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 border border-white/40'
                  }`}>
                    <Lock className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                  </div>
                </div>
                
                <div className="space-y-1 sm:space-y-2">
                  <CardTitle className={`text-2xl sm:text-3xl font-bold transition-colors duration-500 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                  }`}>
                    Reset Password
                  </CardTitle>
                  <CardDescription className={`text-sm sm:text-base transition-colors duration-500 ${
                    theme === 'dark' ? 'text-white/80' : 'text-gray-600'
                  }`}>
                    Enter your email to receive a reset link
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgotEmail" className={`text-sm font-semibold flex items-center gap-2 transition-colors duration-500 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-700'
                    }`}>
                      <User className={`h-4 w-4 transition-colors duration-500 ${
                        theme === 'dark' ? 'text-white/70' : 'text-gray-500'
                      }`} />
                      Email Address
                    </Label>
                    <Input
                      id="forgotEmail"
                      type="email"
                      name="forgotEmail"
                      placeholder="Enter your email address"
                      value={formData.forgotEmail}
                      onChange={handleChange}
                      onKeyPress={handleKeyPress}
                      className={`transition-all duration-500 ${
                        theme === 'dark'
                          ? 'bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white/40 focus:ring-white/20'
                          : 'bg-white/60 border-gray-300 text-gray-800 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-200'
                      }`}
                    />
                    {errors?.forgotEmail && (
                      <Alert variant="destructive" className="bg-red-500/20 border-red-400/30 text-red-200">
                        <AlertDescription className="text-xs sm:text-sm">
                          {errors?.forgotEmail}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </div>

                <Button
                  onClick={handleForgotPassword}
                  type="button"
                  className={`w-full h-10 sm:h-12 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] text-sm sm:text-base ${
                    theme === 'dark'
                      ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                  }`}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-full flex flex-col items-center justify-center space-y-2">
                      <div className="relative w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                        <div className="absolute h-full w-1/3 animate-progress">
                          <img 
                            src="/logo1.png" 
                            alt="Loading" 
                            className="h-full w-full object-contain opacity-80"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      </div>
                      <span className="text-xs">Sending Reset Link...</span>
                    </div>
                  ) : (
                    <>
                      Send Reset Link
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>

                <Separator className={`my-6 transition-colors duration-500 ${
                  theme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
                }`} />
                
                <div className="text-center">
                  <Button 
                    variant="link" 
                    className={`text-sm transition-colors duration-500 ${
                      theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'
                    }`}
                    onClick={handleBackToLogin}
                  >
                    ← Back to Sign In
                  </Button>
                </div>
              </CardContent>
            </div>
          </div>
                </div>
            )}

            {/* Signup Form */}
            {showSignup && (
              <div className="w-full card-zoom-in">
                <div className={`w-full backdrop-blur-xl border shadow-2xl transition-all duration-500 rounded-xl sm:rounded-2xl ${
                  theme === 'dark'
                    ? 'bg-white/10 border-white/20'
                    : 'bg-white/80 border-white/30'
                }`}>
                  <CardHeader className="text-center space-y-1.5 sm:space-y-2.5 p-3 sm:p-4 pt-5 sm:pt-6">
                    <div className="space-y-1">
                      <CardTitle className={`text-xl sm:text-2xl font-bold transition-colors duration-500 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-800'
                      }`}>
                        Create Account
                      </CardTitle>
                      <CardDescription className={`text-xs sm:text-sm transition-colors duration-500 ${
                        theme === 'dark' ? 'text-white/80' : 'text-gray-600'
                      }`}>
                        Sign up to get started
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-4">
                    {errors?.general && (
                      <Alert variant="destructive" className="bg-red-500/20 border-red-400/30 text-black">
                        <AlertDescription className="whitespace-pre-line text-xs sm:text-sm">
                          {errors.general}
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    <div className="space-y-3 sm:space-y-4">
                      <div className="space-y-1 sm:space-y-2">
                        <Label htmlFor="signupName" className={`text-sm font-semibold flex items-center gap-2 transition-colors duration-500 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-700'
                        }`}>
                          <User className={`h-4 w-4 transition-colors duration-500 ${
                            theme === 'dark' ? 'text-white/70' : 'text-gray-500'
                          }`} />
                          Full Name
                        </Label>
                        <Input
                          id="signupName"
                          type="text"
                          name="signupName"
                          placeholder="Enter your full name"
                          value={formData.signupName}
                          onChange={handleChange}
                          className={buildInputClasses(Boolean(errors?.signupName))}
                        />
                      </div>

                      <div className="space-y-1 sm:space-y-2">
                        <Label htmlFor="signupEmail" className={`text-sm font-semibold flex items-center gap-2 transition-colors duration-500 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-700'
                        }`}>
                          <User className={`h-4 w-4 transition-colors duration-500 ${
                            theme === 'dark' ? 'text-white/70' : 'text-gray-500'
                          }`} />
                          Email Address
                        </Label>
                        <Input
                          id="signupEmail"
                          type="email"
                          name="signupEmail"
                          placeholder="Enter your email"
                          value={formData.signupEmail}
                          onChange={handleChange}
                          className={buildInputClasses(Boolean(errors?.signupEmail))}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signupPassword" className={`text-sm font-semibold flex items-center gap-2 transition-colors duration-500 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-700'
                        }`}>
                          <Lock className={`h-4 w-4 transition-colors duration-500 ${
                            theme === 'dark' ? 'text-white/70' : 'text-gray-500'
                          }`} />
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="signupPassword"
                            type={showSignupPassword ? "text" : "password"}
                            name="signupPassword"
                            placeholder="Enter your password"
                            value={formData.signupPassword}
                            onChange={handleChange}
                          className={buildInputClasses(Boolean(errors?.signupPassword), 'pr-12')}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={`absolute right-0 top-0 h-full px-3 py-2 transition-all duration-500 ${
                              theme === 'dark'
                                ? 'hover:bg-white/10 text-white'
                                : 'hover:bg-gray-100 text-gray-500'
                            }`}
                            onClick={() => setShowSignupPassword(!showSignupPassword)}
                          >
                            {showSignupPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signupConfirmPassword" className={`text-sm font-semibold flex items-center gap-2 transition-colors duration-500 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-700'
                        }`}>
                          <Lock className={`h-4 w-4 transition-colors duration-500 ${
                            theme === 'dark' ? 'text-white/70' : 'text-gray-500'
                          }`} />
                          Confirm Password
                        </Label>
                        <Input
                          id="signupConfirmPassword"
                          type={showSignupPassword ? "text" : "password"}
                          name="signupConfirmPassword"
                          placeholder="Confirm your password"
                          value={formData.signupConfirmPassword}
                          onChange={handleChange}
                          className={buildInputClasses(Boolean(errors?.signupConfirmPassword))}
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleSignup}
                      type="button"
                      className={`w-full h-10 sm:h-12 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] text-sm sm:text-base ${
                        theme === 'dark'
                          ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                      }`}
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="w-full flex flex-col items-center justify-center space-y-2">
                          <div className="relative w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                            <div className="absolute h-full left-0 top-0 animate-progress bg-gradient-to-r from-blue-500/50 to-purple-500/50 rounded-full">
                              <img 
                                src="/logo1.png" 
                                alt="Loading" 
                                className="h-full w-auto object-contain opacity-80 absolute right-0"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          </div>
                          <span className="text-xs">Creating Account...</span>
                        </div>
                      ) : (
                        <>
                          Sign Up
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>

                    <Separator className={`my-6 transition-colors duration-500 ${
                      theme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
                    }`} />
                    
                    <div className="text-center">
                      <p className={`text-sm transition-colors duration-500 ${
                        theme === 'dark' ? 'text-white/80' : 'text-gray-600'
                      }`}>
                        Already have an account?{" "}
                        <Button 
                          variant="link" 
                          onClick={handleBackToLoginFromSignup}
                          className={`p-0 font-medium transition-colors duration-500 ${
                            theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'
                          }`}
                        >
                          Sign In
                        </Button>
                      </p>
                    </div>
                  </CardContent>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
